import { ControllerManager } from "webrtc-player/controllerManager/controllerManager";
import {
  ReactNode,
  createContext,
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";
import { SignalContext } from "./SignalContext";
import { AuthContext } from "./AuthContext";
import { ContextArgs } from "webrtc-player/controllerManager/contextArgs";
import { GeneralController } from "webrtc-player/webrtc_controller/general_controller";
import { DeviceSnapshotType } from "webrtc-player/webrtc/data_webrtc";
import { DB_STORE } from "lib/constants";
import type { SnapshotDataTypes } from "pages/service";
import { DataWebRTC } from "webrtc-player/webrtc/data_webrtc";
import { DataControlerContext } from "context/DataControlerContext";
import { isSupportSinglePcV2 } from "lib/utils";
import { DataController } from "webrtc-player/webrtc_controller/data_controller";

type SinglePcContolerContextType = {
  getController: (
    nvrId,
    deviceIdV2,
    orgId,
  ) => Promise<GeneralController | DataController>;
  getSinglePcControllerByNvrId: (
    nvrId,
    deviceIdV2,
    orgId,
  ) => Promise<GeneralController>;
};
const initialValue = {
  getSinglePcControllerByNvrId: () => Promise.resolve(null),
  getController: () => Promise.resolve(null),
};
export const SinglePcManagerContext =
  createContext<SinglePcContolerContextType>(initialValue);

let waiters = [];
const singlePcManagerMap = new Map();
export default function SinglePcContolerProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const { signal, sessionId, wsIsOpen, getDb } = useContext(SignalContext);
  const { getControllerManagerWithWaiter } = useContext(DataControlerContext);
  const { user } = useContext(AuthContext);
  const [paramsFullReady, setParamsFullReady] = useState(false);
  async function resolveWaiters() {
    for (const waiter of waiters) {
      if (singlePcManagerMap.has(`${waiter.deviceId}-${waiter.orgId}`)) {
        const manager = singlePcManagerMap.get(
          `${waiter.deviceId}-${waiter.orgId}`,
        );
        const controller = await manager.asyncGetController();
        waiter.resolve(controller);
        continue;
      }
      const singlePcManager = new ControllerManager(
        waiter.nvrId,
        waiter.orgId,
        waiter.deviceId,
      );
      singlePcManagerMap.set(
        `${waiter.deviceId}-${waiter.orgId}`,
        singlePcManager,
      );
      const controller = await singlePcManager.asyncGetController();
      resubscribeToDb(controller);
      waiter.resolve(controller);
    }
    waiters = [];
  }

  const resubscribeToDb = (controller) => {
    controller.webRTC.removeEventListener(
      DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
      snapshotToDb,
    );
    controller.webRTC.addEventListener(
      DataWebRTC.EVENT_DEVICE_SNAPSHOTS,
      snapshotToDb,
    );
  };

  useEffect(() => {
    if (paramsFullReady) {
      resolveWaiters();
    }
  }, [paramsFullReady]);
  useEffect(() => {
    // stop second controller manager creation
    if (!sessionId || !signal || !wsIsOpen || !user) {
      setParamsFullReady(false);
      return;
    }
    setParamsFullReady(true);
    //set global params for single pc manager
    ContextArgs.sessionId = sessionId;
    ContextArgs.signal = signal;
    ContextArgs.token = user?.token;
    ContextArgs.retryLimit = 999999;
    ContextArgs.retryInterval = 3000;
    ContextArgs.ENV = process.env?.NODE_ENV;
  }, [sessionId, signal, wsIsOpen, user]);

  const snapshotToDb = useCallback(
    (camera: DeviceSnapshotType) => {
      return new Promise<SnapshotDataTypes>(async (resolve, reject) => {
        if (
          camera.snapshot.imageBitmap &&
          camera.snapshot.imageBitmap.width > 0
        ) {
          const canvas = document.createElement("canvas");
          const width = (canvas.width = camera.snapshot.imageBitmap.width);
          const height = (canvas.height = camera.snapshot.imageBitmap.height);
          const context = canvas.getContext("bitmaprenderer");
          const newImageBitmap = await createImageBitmap(
            camera.snapshot.imageBitmap,
          );
          context.transferFromImageBitmap(newImageBitmap);
          canvas.toBlob(async (blob) => {
            resolve({
              blob,
              id: camera?.camera?.id,
              macAddress: camera?.camera?.macAddress,
              remoteIndex: camera?.camera?.remoteIndex,
              width,
              height,
            });
            if (Number.isInteger(camera?.camera?.id)) {
              getDb().then((db) => {
                try {
                  db.put(DB_STORE.SNAPSHOT, {
                    id: +camera.camera?.id,
                    imageBlob: blob,
                    width: width,
                    height: height,
                  });
                  console.log(
                    "lzctest:Put snapshot success",
                    camera.camera?.id,
                  );
                } catch (error) {
                  console.log("put snapshot error:", error);
                }
                canvas.remove();
              });
            }
          });
        }
      });
    },
    [getDb],
  );

  const getSinglePcControllerByNvrId = (
    _nvrId,
    _orgId,
    deviceIdV2?,
  ): Promise<GeneralController> => {
    const orgId = +_orgId;
    const deviceId = deviceIdV2 ?? +_nvrId;
    if (singlePcManagerMap.has(`${deviceId}-${orgId}`)) {
      const manager = singlePcManagerMap.get(`${deviceId}-${orgId}`);
      return manager.asyncGetController();
    } else {
      return new Promise(async (resolve) => {
        if (paramsFullReady) {
          if (singlePcManagerMap.has(`${deviceId}-${orgId}`)) {
            const manager = singlePcManagerMap.get(`${deviceId}-${orgId}`);
            return manager.asyncGetController();
          }
          const singlePcManager = new ControllerManager(
            _nvrId,
            orgId,
            deviceIdV2,
          );
          singlePcManagerMap.set(`${deviceId}-${orgId}`, singlePcManager);
          const controller = await singlePcManager.asyncGetController();
          resubscribeToDb(controller);
          resolve(controller);
        } else {
          waiters.push({ nvrId: _nvrId, deviceId, orgId, resolve });
          console.log("push into waiter");
        }
      });
    }
  };

  const getController = async (nvrId, deviceId, orgId) => {
    if (await isSupportSinglePcV2(deviceId, orgId)) {
      const singlePcController = await getSinglePcControllerByNvrId(
        nvrId,
        orgId,
        deviceId,
      );
      return singlePcController;
    } else {
      const dataControllerManager = await getControllerManagerWithWaiter();
      const controller = await dataControllerManager.asyncGetDataController(
        nvrId,
        orgId,
      );
      return controller;
    }
  };

  return (
    <SinglePcManagerContext.Provider
      value={{
        getSinglePcControllerByNvrId: getSinglePcControllerByNvrId,
        getController,
      }}
    >
      {children}
    </SinglePcManagerContext.Provider>
  );
}
